import { useMemo, useState } from "react"
import { Button, Paper, Collapse, Typography } from "@mui/material"
import { KeyboardArrowDown } from "@mui/icons-material"
import "./mapRotationSelector.scss"
import { useTranslation } from "react-i18next"
import { BaseCheckbox } from "@/components/form/checkbox/Checkbox"
import { useUpdateSessionRotationMutation } from "@/hooks/api/useSessionQuery"
import BasicButton from "@/components/button/BasicButton"

interface SessionRotation {
  idfSession: number
  name: string
  isRoration: boolean
  startMinutes: number
  endMinutes: number
}

export default function MapRotationSelector({
  sessionRotationInfo,
  setSessionRotationInfo,
}: {
  sessionRotationInfo: SessionRotation[]
  setSessionRotationInfo: (sessionRotationInfo: SessionRotation[]) => void
}) {
  const { t } = useTranslation()
  const { mutateAsync } = useUpdateSessionRotationMutation()

  const [isOpen, setIsOpen] = useState(false)
  const [tempSessionRotationInfo, setTempSessionRotationInfo] = useState<SessionRotation[]>([])

  const applyDisabled = useMemo(() => {
    return tempSessionRotationInfo.every((temp) => {
      const original = sessionRotationInfo.find((s) => s.idfSession === temp.idfSession)

      return original?.isRoration === temp.isRoration
    })
  }, [tempSessionRotationInfo, sessionRotationInfo])

  const handleToggle = () => {
    setIsOpen(!isOpen)
    setTempSessionRotationInfo(sessionRotationInfo.map((s) => ({ ...s })))
  }

  const handleCheckbox = (idfSession: number) => {
    setTempSessionRotationInfo(
      tempSessionRotationInfo.map((session) => {
        if (session.idfSession === idfSession) {
          return { ...session, isRoration: !session.isRoration }
        }

        return session
      }),
    )
  }

  const handleApply = () => {
    const payload = tempSessionRotationInfo.map(({ idfSession, isRoration }) => ({
      idfSession,
      rotated: isRoration,
    }))
    setIsOpen(false)
    setSessionRotationInfo(tempSessionRotationInfo)
    mutateAsync(payload)
  }

  return (
    <div className="rotation-selector">
      <Button
        className="rotation-selector-button"
        onClick={handleToggle}
        variant="outlined"
        fullWidth
        startIcon={
          <img src="/images/ri-reset-left-line.png" alt="Rotate" className="rotation-image" />
        }
        endIcon={<KeyboardArrowDown className={`arrow-icon ${isOpen ? "open" : ""}`} />}>
        {t("rotateSession")}
      </Button>

      <Collapse in={isOpen}>
        <Paper className="rotation-dropdown">
          {tempSessionRotationInfo.map((session) => {
            return (
              <div
                key={session.idfSession}
                className="rotation-checkbox-item"
                onClick={() => handleCheckbox(session.idfSession)}>
                <BaseCheckbox checked={session.isRoration} />
                <Typography className="rotation-checkbox-item-text" variant="body2">
                  {session.name}
                </Typography>
              </div>
            )
          })}

          <BasicButton
            disabled={applyDisabled}
            className="apply-button"
            type="primary-contained"
            onClick={handleApply}>
            {t("button.apply")}
          </BasicButton>
        </Paper>
      </Collapse>
    </div>
  )
}
